<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { TranslateResult } from "vue-i18n";
import { ComboboxOptionModel } from "../../services/models/combobox.models";
import autoAnimate from "@formkit/auto-animate";

const props = defineProps<{
  heading: string | TranslateResult;
  hideHeading?: boolean;
  options: ComboboxOptionModel[];
  selectedId?: string;
  ariaLiveText?: string;
}>();

const emits = defineEmits(["update:modelValue"]);
const buttonRefs = ref<HTMLElement[]>([]);
const rootElement = ref();

const selectOption = (id: string | number) => {
    emits("update:modelValue", id);
}

const focusButton = (index: number) => {
    const el = buttonRefs.value[index];
    if (el) el.focus();
};

const onOptionKeydown = (e: KeyboardEvent, index: number) => {
    if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        selectOption(props.options[index].id);
    }
    if (e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex = index < props.options.length - 1 ? index + 1 : 0;
        focusButton(nextIndex);
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prevIndex = index > 0 ? index - 1 : props.options.length - 1;
        focusButton(prevIndex);
    } else if (e.key === "Home") {
        e.preventDefault();
        focusButton(0);
    } else if (e.key === "End") {
        e.preventDefault();
        focusButton(props.options.length - 1);
    }
};

onMounted(() => {
  autoAnimate(rootElement.value);
});
</script>

<template>
    <div>
        <div ref="rootElement">
            <span v-if="props.ariaLiveText" aria-live="polite" class="sr-only">{{ props.ariaLiveText }}</span>
            <div v-if="props.options.length" class="buttonlist">
                <h2 v-if="!props.hideHeading" class="buttonlist__heading">
                    {{ props.heading }}
                </h2>
                <ul
                    class="buttonlist__menu"
                    :aria-label="typeof props.heading === 'string' ? props.heading : undefined"
                >
                    <li
                        v-for="(option, index) in props.options"
                        :key="option.id"
                        class="buttonlist__option"
                    >
                        <button
                            class="buttonlist__option-content"
                            @click="selectOption(option.id)"
                            @keydown="onOptionKeydown($event, index)"
                            ref="buttonRefs"
                        >
                            <strong v-if="option.heading" class="buttonlist__heading">
                                {{ option.heading }}
                            </strong>
                            <span v-if="option.description" class="buttonlist__description">
                                ({{ option.description }})
                            </span>
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>